import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Brain } from 'lucide-react'; // Changed from Camera to Brain for AI theme

const AIImgToOrderButton = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [pulsePhase, setPulsePhase] = useState(0);
  const [sparkleAngles, setSparkleAngles] = useState([0, 45, 90, 135, 180, 225, 270, 315]);

  // Animation for pulse effect
  useEffect(() => {
    let animationFrame;

    const animate = () => {
      setPulsePhase((prev) => (prev + 0.01) % 1);
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  // Animation for sparkle rotation
  useEffect(() => {
    if (isHovered) {
      const interval = setInterval(() => {
        setSparkleAngles((prev) => prev.map((angle) => (angle + 2) % 360));
      }, 50);

      return () => clearInterval(interval);
    }
  }, [isHovered]);

  return (
    <Link to="/order/image-to-order" style={{ textDecoration: 'none', display: 'inline-block' }}>
      <div
        style={{ position: 'relative', display: 'inline-flex', marginTop: '8px' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Outer glow and pulse container */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            borderRadius: '9999px',
            overflow: 'hidden',
            pointerEvents: 'none',
            opacity: isHovered ? 0.8 : 0.6,
          }}
        >
          {/* Animated pulse effect */}
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              transform: `scale(${1.5 + pulsePhase * 0.5})`,
              background: `radial-gradient(circle, rgba(0, 191, 255, ${0.7 - pulsePhase * 0.7}) 0%, rgba(138, 43, 226, ${0.5 - pulsePhase * 0.5}) 40%, transparent 70%)`,
              top: '50%',
              left: '50%',
              width: '200%',
              height: '200%',
              marginTop: '-100%',
              marginLeft: '-100%',
            }}
          />
        </div>

        {/* Sparkles - only visible on hover */}
        {isHovered &&
          sparkleAngles.map((angle, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: '2px',
                height: i % 2 === 0 ? '8px' : '5px',
                backgroundColor: i % 3 === 0 ? '#00BFFF' : '#8A2BE2',
                top: '50%',
                left: '50%',
                transform: `rotate(${angle}deg) translate(${28 + Math.sin(pulsePhase * Math.PI * 2) * 4}px, 0)`,
                opacity: 0.6,
                borderRadius: '2px',
                boxShadow: `0 0 5px ${i % 3 === 0 ? '#00BFFF' : '#8A2BE2'}`,
                zIndex: 0,
              }}
            />
          ))}

        {/* Main button */}
        <button
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            fontWeight: 'bold',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '9999px',
            transition: 'all 0.3s',
            background: 'linear-gradient(45deg, #00BFFF, #8A2BE2)',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)',
            boxShadow: isHovered
              ? '0 0 20px rgba(0, 191, 255, 0.8), 0 0 40px rgba(138, 43, 226, 0.6)'
              : '0 0 10px rgba(0, 191, 255, 0.5), 0 0 20px rgba(138, 43, 226, 0.3)',
            minWidth: '160px',
            zIndex: 2,
            fontSize: '0.95rem',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          {/* AI Icon with animation */}
          <div style={{ position: 'relative' }}>
            <Brain
              style={{
                marginRight: '4px',
                transform: isHovered ? `translateY(${Math.sin(Date.now() / 300) * 2}px)` : 'none',
                filter: isHovered ? 'drop-shadow(0 0 5px rgba(255,255,255,0.9))' : 'none',
              }}
              size={20}
              strokeWidth={2.5}
            />
            {isHovered && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  animation: 'ping 1s infinite',
                  borderRadius: '9999px',
                  backgroundColor: 'white',
                  opacity: 0.3,
                }}
              />
            )}
          </div>

          {/* Text with subtle floating animation */}
          <span
            style={{
              position: 'relative',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              textShadow: isHovered ? '0 0 8px rgba(255,255,255,0.9)' : 'none',
              transform: isHovered ? `translateY(${Math.sin(Date.now() / 500) * 1.5}px)` : 'none',
            }}
          >
            AI Image to Order
          </span>

          {/* Highlighted "NEW!" badge */}
          <div
            style={{
              position: 'absolute',
              top: '-6px', // Adjusted to add a little gap
              right: '-4px',
              backgroundColor: '#FFD700',
              color: 'white',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              padding: '2px 6px',
              borderRadius: '9999px',
              transform: `scale(${isHovered ? 1.1 : 1}) ${isHovered ? `rotate(${Math.sin(Date.now() / 400) * 5}deg)` : ''}`,
              boxShadow: '0 0 10px rgba(255, 215, 0, 0.8)',
              animation: 'bounce 1.5s infinite',
              zIndex: 3,
            }}
          >
            NEW!
          </div>
        </button>
      </div>
    </Link>
  );
};

export default AIImgToOrderButton;